import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  CurrencyDollarIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { AUDIT_ACTIONS } from '../../services/system/audit';

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  let date;
  if (ts.toDate) date = ts.toDate();
  else if (ts.seconds) date = new Date(ts.seconds * 1000);
  else date = new Date(ts);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const ActionIcon = ({ action }) => {
  const cls = 'w-4 h-4';
  switch (action) {
    case AUDIT_ACTIONS.DELETE:
    case AUDIT_ACTIONS.PAYMENT_DELETED:
      return <TrashIcon className={`${cls} text-red-600`} />;
    case AUDIT_ACTIONS.CREATE:
      return <PlusCircleIcon className={`${cls} text-green-600`} />;
    case AUDIT_ACTIONS.PRICE_CHANGE:
    case AUDIT_ACTIONS.PAYMENT_RECORDED:
    case AUDIT_ACTIONS.LEDGER_ADJUSTMENT:
      return <CurrencyDollarIcon className={`${cls} text-amber-600`} />;
    case AUDIT_ACTIONS.COMMISSION_CHANGE:
      return <CurrencyDollarIcon className={`${cls} text-purple-600`} />;
    case AUDIT_ACTIONS.ROLE_CHANGE:
    case AUDIT_ACTIONS.PERMISSION_CHANGE:
      return <ShieldCheckIcon className={`${cls} text-indigo-600`} />;
    case AUDIT_ACTIONS.LOGIN:
    case AUDIT_ACTIONS.LOGOUT:
      return <UserCircleIcon className={`${cls} text-gray-600`} />;
    case AUDIT_ACTIONS.GENERATE:
      return <DocumentArrowDownIcon className={`${cls} text-blue-600`} />;
    case AUDIT_ACTIONS.STATUS_CHANGE:
      return <ArrowPathIcon className={`${cls} text-blue-600`} />;
    case AUDIT_ACTIONS.UPDATE:
    default:
      return <PencilSquareIcon className={`${cls} text-blue-600`} />;
  }
};

const tagBadgeClass = (tag) => {
  switch (tag) {
    case 'financial': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'deletions': return 'bg-red-50 text-red-700 border-red-200';
    case 'trip_changes': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'driver_changes': return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'customer_changes': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'documents': return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    case 'user_access': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

const renderValue = (v) => {
  if (v === null || v === undefined) return <em className="text-gray-400">—</em>;
  if (typeof v === 'object') {
    return <code className="text-xs break-all">{JSON.stringify(v)}</code>;
  }
  return <span className="break-all">{String(v)}</span>;
};

const ChangesTable = ({ changes, t }) => {
  const keys = Object.keys(changes || {});
  if (keys.length === 0) return null;
  return (
    <table className="w-full text-xs border border-gray-200 rounded">
      <thead className="bg-gray-50">
        <tr>
          <th className="text-start px-2 py-1 font-medium text-gray-600">{t('audit.field', 'Field')}</th>
          <th className="text-start px-2 py-1 font-medium text-gray-600">{t('audit.old_value', 'Old value')}</th>
          <th className="text-start px-2 py-1 font-medium text-gray-600">{t('audit.new_value', 'New value')}</th>
        </tr>
      </thead>
      <tbody>
        {keys.map(k => (
          <tr key={k} className="border-t border-gray-100">
            <td className="px-2 py-1 font-medium text-gray-700 align-top">{k}</td>
            <td className="px-2 py-1 text-gray-600 align-top max-w-xs">{renderValue(changes[k].from)}</td>
            <td className="px-2 py-1 text-gray-900 align-top max-w-xs">{renderValue(changes[k].to)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Build a route for the entry's primary record, when we know how to link it.
// Falls back to null (plain text rendering) for modules without a detail page.
const recordRouteFor = (entry) => {
  if (!entry?.recordId) return null;
  switch (entry.module) {
    case 'trip': return `/shipments/${entry.recordId}`;
    case 'customer': return `/clients/${entry.recordId}`;
    case 'driver': return `/drivers/${entry.recordId}`;
    default: return null;
  }
};

// Related-entity links extracted from meta (e.g. a payment entry references
// its driver, a commission change references its driver and trip).
const relatedLinksFrom = (meta, { driverNameById = {}, customerNameById = {} } = {}) => {
  if (!meta) return [];
  const links = [];
  if (meta.driverId) {
    links.push({
      to: `/drivers/${meta.driverId}`,
      label: driverNameById[meta.driverId] || meta.driverName || meta.driverId,
      kind: 'driver'
    });
  }
  if (meta.customerId) {
    links.push({
      to: `/clients/${meta.customerId}`,
      label: customerNameById[meta.customerId] || meta.customerName || meta.customerId,
      kind: 'customer'
    });
  }
  if (meta.shipmentId || meta.tripId) {
    const id = meta.shipmentId || meta.tripId;
    links.push({
      to: `/shipments/${id}`,
      label: meta.tripNumber || meta.trackingNumber || id,
      kind: 'trip'
    });
  } else if (meta.tripNumber || meta.trackingNumber) {
    links.push({ label: meta.tripNumber || meta.trackingNumber, kind: 'trip' });
  }
  return links;
};

const AuditEntryRow = ({ entry, showModule = true, driverNameById, customerNameById }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const recordRoute = recordRouteFor(entry);
  const relatedLinks = relatedLinksFrom(entry.meta, { driverNameById, customerNameById });

  // Prefer a resolved name over the raw id when this entry is itself about a
  // driver or customer (e.g. driver edit shows id as recordLabel by default).
  const resolvedRecordLabel = (() => {
    if (entry.module === 'driver' && driverNameById && driverNameById[entry.recordId]) {
      return driverNameById[entry.recordId];
    }
    if (entry.module === 'customer' && customerNameById && customerNameById[entry.recordId]) {
      return customerNameById[entry.recordId];
    }
    return entry.recordLabel || entry.recordId;
  })();

  const hasDetails = (entry.changes && Object.keys(entry.changes).length > 0)
    || entry.before || entry.after || entry.meta;

  return (
    <>
      <tr className="border-b border-gray-100 hover:bg-gray-50">
        <td className="px-3 py-2 align-top">
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-gray-400 hover:text-gray-700"
            disabled={!hasDetails}
            aria-label="toggle"
          >
            {hasDetails ? (expanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />) : <span className="inline-block w-4 h-4" />}
          </button>
        </td>
        <td className="px-3 py-2 align-top whitespace-nowrap text-xs text-gray-600">
          {formatTimestamp(entry.timestamp)}
        </td>
        <td className="px-3 py-2 align-top text-sm">
          <div className="flex items-center gap-2">
            <ActionIcon action={entry.action} />
            <span className="font-medium text-gray-900">
              {t(`audit.action.${entry.action}`, entry.action)}
            </span>
          </div>
        </td>
        {showModule && (
          <td className="px-3 py-2 align-top text-sm text-gray-700">
            {t(`audit.module.${entry.module}`, entry.module)}
          </td>
        )}
        <td className="px-3 py-2 align-top text-sm text-gray-700">
          <div className="font-medium">
            {recordRoute ? (
              <Link to={recordRoute} className="text-blue-600 hover:underline">
                {resolvedRecordLabel}
              </Link>
            ) : (
              resolvedRecordLabel || '—'
            )}
          </div>
          {entry.recordId && entry.recordLabel && (
            <div className="text-xs text-gray-400">{entry.recordId}</div>
          )}
          {relatedLinks.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
              {relatedLinks.map((l, i) => (
                <span key={i} className="text-gray-500">
                  <span className="text-gray-400">{t(`audit.related.${l.kind}`, l.kind)}:</span>{' '}
                  {l.to ? (
                    <Link to={l.to} className="text-blue-600 hover:underline">{l.label}</Link>
                  ) : (
                    <span>{l.label}</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="px-3 py-2 align-top text-sm text-gray-700">
          <div className="font-medium">{entry.actorName || entry.actorEmail || '—'}</div>
          {entry.actorRole && <div className="text-xs text-gray-400">{entry.actorRole}</div>}
        </td>
        <td className="px-3 py-2 align-top">
          <div className="flex flex-wrap gap-1">
            {(entry.tags || []).filter(tg => tg !== 'all').map(tg => (
              <span key={tg} className={`text-[10px] px-2 py-0.5 rounded-full border ${tagBadgeClass(tg)}`}>
                {t(`audit.tag.${tg}`, tg)}
              </span>
            ))}
          </div>
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr className="bg-gray-50">
          <td colSpan={showModule ? 7 : 6} className="px-6 py-3">
            <div className="space-y-3">
              {entry.changes && Object.keys(entry.changes).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">
                    {t('audit.changes', 'Changes')}
                  </div>
                  <ChangesTable changes={entry.changes} t={t} />
                </div>
              )}
              {entry.action === 'create' && entry.after && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-600 font-semibold">
                    {t('audit.created_snapshot', 'Created record snapshot')}
                  </summary>
                  <pre className="mt-1 p-2 bg-white border border-gray-200 rounded overflow-x-auto">
                    {JSON.stringify(entry.after, null, 2)}
                  </pre>
                </details>
              )}
              {(entry.action === 'delete' || entry.action === 'payment_deleted') && entry.before && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer text-gray-600 font-semibold">
                    {t('audit.deleted_snapshot', 'Deleted record snapshot')}
                  </summary>
                  <pre className="mt-1 p-2 bg-white border border-red-200 rounded overflow-x-auto">
                    {JSON.stringify(entry.before, null, 2)}
                  </pre>
                </details>
              )}
              {entry.meta && Object.keys(entry.meta).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-600 font-semibold">
                    {t('audit.meta', 'Additional context')}
                  </summary>
                  <pre className="mt-1 p-2 bg-white border border-gray-200 rounded overflow-x-auto">
                    {JSON.stringify(entry.meta, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default AuditEntryRow;
